import {Component, EventEmitter, Input, NO_ERRORS_SCHEMA, OnDestroy, OnInit, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {FaIconLibrary, FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {CheckBoxModule, InputsModule} from '@progress/kendo-angular-inputs';
import {ButtonsModule} from '@progress/kendo-angular-buttons';
import {
  DialogCloseResult,
  DialogContentBase,
  DialogRef,
  DialogService,
  DialogsModule,
  WindowModule,
  WindowRef
} from '@progress/kendo-angular-dialog';
import {NotificationModule, NotificationService} from '@progress/kendo-angular-notification';
import {KENDO_PAGER, PagerModule} from '@progress/kendo-angular-pager';
import {GridModule, PageChangeEvent, PagerPosition, PagerType} from '@progress/kendo-angular-grid';
import {MulticheckFilterComponent} from '../../filters/multicheck-filter/multicheck-filter.component';

import {Observable, Subscription} from 'rxjs';
import {LoggerService} from '../../shared/logger.service';
import {MeService} from '../../auth/me.service';
import {ToastrService} from 'ngx-toastr';
import {faArrowDown, faArrowUp, faEdit, faPlusCircle} from '@fortawesome/free-solid-svg-icons';
import {CasiCloseOptions, FormModes} from '../../common/constants.lib';
import {DeleteConfirmComponent} from '../../common-components/delete-confirm/delete-confirm.component';
import {ErrorDialogModalComponent} from '../../common-components/error-dialog-modal/error-dialog-modal.component';
import {
  Organisation,
  OrganisationNew, OrganisationType,
  OrganisationUser,
  OrganisationUserNew,
  OrganisationUserType
} from '../../models/models.lib';
import {CompositeFilterDescriptor, distinct, filterBy, orderBy, SortDescriptor} from '@progress/kendo-data-query';
import {OrganisationUserService} from '../organisation-user.service';
import {OrganisationEditComponent} from '../organisation-edit.component/organisation-edit.component';
import {setTitle} from '../../common/utils.lib';
import {OrganisationUserAddComponent} from '../organisation-user-add.component/organisation-user-add.component';

@Component({
  selector: 'app-organisation-users-win',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule, ReactiveFormsModule,
    FontAwesomeModule,
    InputsModule,
    ButtonsModule,
    DialogsModule,
    WindowModule,
    NotificationModule,
    CheckBoxModule,
    KENDO_PAGER,
    PagerModule,
    GridModule,
    MulticheckFilterComponent
  ],
  schemas: [NO_ERRORS_SCHEMA],
  templateUrl: './organisation-users-win.html',
  styleUrl: './organisation-users-win.scss'
})
export class OrganisationUsersWin  implements OnInit, OnDestroy{
  private _organisation: Organisation | undefined;

  @Input() title: string = 'Title Not Set';

  @Input({ required: true }) set organisation(val: Organisation) {
    this._organisation = val;
  }
  get organisation(): Organisation | undefined {
    return this._organisation;
  };

  @Output() onClose = new EventEmitter<void>();

  private _windowRef: WindowRef | undefined;

  public view: Observable<OrganisationUser[]> = new Observable<OrganisationUser[]>();
  public rawData: OrganisationUser[] = [];
  public filter: CompositeFilterDescriptor = { logic: "and", filters: [] };
  public gridData: OrganisationUser[] = filterBy(this.rawData, this.filter);

  public currentItem: OrganisationUserType = OrganisationUserNew.create(this.organisation);

  public pagerTypes = ["numeric", "input"];
  public type: PagerType = "numeric";
  public info = true;
  public pageSizes = true;
  public previousNext = true;
  public position: PagerPosition = "bottom";

  public pageSize = 10;

  private _subs: Subscription[] = [];
  public formMode: FormModes = FormModes.NOTSET;

  constructor(
    private logger: LoggerService,
    private notificationService: NotificationService,
    private me: MeService,
    public dataService: OrganisationUserService,
    public faIcons: FaIconLibrary,
    private dialogService: DialogService,
    private toastr: ToastrService
  ) {
    faIcons.addIcons(
      faPlusCircle, faEdit, faArrowUp, faArrowDown
    );
  }

  public sort: SortDescriptor[] = [
    { field: "discipline_name", dir: "asc" },
  ];

  ngOnInit(): void {
    this.logger.debug('ngOnInit...');
    if(this.organisation)
      this.dataService.query(this.organisation.id);

    this._subs.push(this.dataService.subscribe(e => {
      this.logger.debug('ngOnInit:v: ', e);
      this.rawData = e;
      this.loadData();
    }));
  }

  ngOnDestroy(): void {
    this.logger.debug('ngOnDestroy...');
    this._subs.forEach(s => s.unsubscribe());
  }


  public pageChange(event: PageChangeEvent): void {
    this.logger.debug(`pageChange: `, event);
  }

  onClearFilters(gridName: string) {
    this.logger.debug("onClearFilters: ", gridName);
    this.filter = { logic: "and", filters: [] };
    this.loadData();
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.logger.debug("sortChange: ", sort);
    this.sort = sort;
    this.loadData();
  }

  public filterChange(filter: CompositeFilterDescriptor): void {
    this.logger.debug('filterChange: filter: ', filter);
    this.filter = filter;
    this.loadData();
  }

  public distinctPrimitive(fieldName: string): any[] {
    this.logger.debug('distinctPrimitive: fieldName: ', fieldName);
    return distinct(this.gridData, fieldName).map((item: any) => item[fieldName]);
  }

  public onCancelAction(): void {
    this.logger.debug(`onCancelAction...`);
    this.onClose.emit();
  }

  onConfirmDelete(dataItem: any): void {
    this.logger.debug("onConfirmDelete: ", dataItem);
    this.currentItem = OrganisationUser.create(dataItem);
    this.openConfirmDailog();
  }

  public onAddClick(): void {
    this.logger.debug("onAdd...");
    this.currentItem = OrganisationUserNew.create(this.organisation);
    this.formMode = FormModes.NEW;
    this.logger.debug("onAdd:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem);
  }

  onEditClick(dataItem: any): void {
    this.logger.debug("onEditClick: ", dataItem);
    this.currentItem = OrganisationUser.create(dataItem);
    this.formMode = FormModes.EDIT;
    this.logger.debug("onEditClick:currentItem ", this.currentItem);
    this.openAddEditDialog(this.formMode, this.currentItem);
  }

  private openAddEditDialog(mode: FormModes, dataItem: OrganisationUserType): void {
    this.logger.debug("openAddEditDialog...");
    const dialogRef = this.dialogService.open({
      content: OrganisationUserAddComponent,
      minWidth: 320,
      width: 600,
    });

    const contentRef = dialogRef.content.instance as OrganisationUserAddComponent;
    contentRef.title = setTitle(mode, 'ORGANISATION USER');
    contentRef.organisationUser = dataItem;
    contentRef.mode = mode;
    // contentRef.selectedRoleIds = dataItem.roles;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openAddEditDialog:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Saved) {
        this.logger.debug("openAddEditDialog:saved... ", contentRef.editForm.value);
        this.handleClose(contentRef.editForm.value, mode);
      }
    });
  }

  private handleClose(data: any, mode: FormModes): void {
    this.logger.debug('handleClose...', data);

    let dup: boolean = false;
    if (data) {
      let inUse = this.rawData.find(r => r.email === data.email);
      this.logger.debug('handleClose...inUse', inUse);
      if (inUse) {
        if (mode === FormModes.NEW) {
          dup = true;
        } else {
          dup = inUse.id !== data.id;
        }
      }
      if (dup) {
        this.logger.warn(`handleClose: discipline: ${data.email} already in use `);
        this.openErrorDailog("Duplicate Organisation User Email", "That Organisation User already exists!", "Close and Retry")
      } else
        this.save(data, mode);
    }
  }

  private save(organisationUser: any, mode: FormModes): void {
    this.logger.debug('saveDiscipline...', organisationUser, mode);
    organisationUser.user_roles = organisationUser.user_roles ? organisationUser.user_roles.map((d:any) => d.id) : [];
    if (mode === FormModes.EDIT) {
      this.dataService.update(organisationUser.id, organisationUser)
        .then(resp => {
          this.logger.debug('saveDiscipline:update:resp: ', resp);
          this.notify("Organisation User Updated");
          this.closeWindowRef();
          if(this.organisation)
            this.dataService.query(this.organisation.id);
        })
        .catch(err => {
          this.logger.error('saveDiscipline:update:err: ', err);
          this.notify("Error Updating Organisation User", true);
        });
    } else {
      this.dataService.add(organisationUser)
        .then(resp => {
          this.logger.debug('saveDiscipline:add:resp: ', resp);
          this.notify("Organisation User Added");
          this.closeWindowRef();
          if(this.organisation)
            this.dataService.query(this.organisation.id);
        })
        .catch(err => {
          this.logger.error('saveDiscipline:add:err: ', err);
          this.notify("Error Creating Organisation User", true);
        });
    }
  }


  private openConfirmDailog(): void {
    this.logger.debug("openConfirmDailog: ", this.currentItem);
    const dialogRef = this.dialogService.open({
      content: DeleteConfirmComponent,
      minWidth: 250,
      width: 450
    });

    const contentRef = dialogRef.content.instance as DeleteConfirmComponent;
    contentRef.title = "CONFIRM DELETE";
    contentRef.content = `Delete User - <b>${this.currentItem?.email}</b>?`;

    dialogRef.result.subscribe((r: any) => {
      this.logger.debug('openDialogForm:result:', r);
      if (r instanceof DialogCloseResult) {
        return;
      }
      if (r.text === CasiCloseOptions.Ok) {
        this.logger.debug("openConfirmDailog:ok to proceed... ", this.currentItem);
        this.destroy(this.currentItem);
      }
    });
  }

  private destroy(organisationUser: any): void {
    this.logger.debug('save...', organisationUser);
    this.dataService.destroy(organisationUser.id)
      .then(resp => {
        this.logger.debug('save:update:resp: ', resp);
        this.notify("Organisation User Deleted");
        this.closeWindowRef();
        if(this._organisation)
          this.dataService.query(this._organisation.id);
      })
      .catch(err => {
        this.logger.error('saveDiscipline:update:err: ', err);
        this.notify("Error Deleting Organisation User", true);
      });
  }

  private notify(msg: string, error: boolean = false): void {
    this.logger.debug("notify", msg);
    if (error)
      this.toastr.error(msg);
    else
      this.toastr.success(msg);
  }

  private openErrorDailog(title: string = 'Not set', content: string = 'No content', buttonText: string = 'Close'): void {
    const dialogRef = this.dialogService.open({
      content: ErrorDialogModalComponent,
      minWidth: 320,
      width: 750,
      cssClass: "custom-css-class"
    });

    const contentRef = dialogRef.content.instance as ErrorDialogModalComponent;
    contentRef.title = title;
    contentRef.content = content;
    contentRef.buttonText = buttonText;
  }


  private closeWindowRef(): void {
    if (this._windowRef) {
      this._windowRef.close();
      this._windowRef = undefined;
    }
    // this.editWinOpen = false;
  }

  private loadData(): void {
    this.gridData = orderBy(filterBy(this.rawData, this.filter), this.sort);
    this.pageSize = this.gridData.length;
  }
}
